import { supabase } from "@/supabase";
import { getPagination } from "../utils/general";
import { reactive } from "vue";
import { openDB } from "idb";

const _COMMON = "common_view";
const DB_NAME = "BrandripplrApp";
const STORE_NAME = "common_cache";

async function initDB() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
}

async function saveToDB(key, value) {
  const db = await initDB();
  await db.put(STORE_NAME, value, key);
}

async function getFromDB(key) {
  const db = await initDB();
  return db.get(STORE_NAME, key);
}

export const CommonData = reactive({
  countries: [],
  states: [],
  cities: [],
  categories: [],
  languages: [],
  nationalities: [],
  professions: [],
  tags: [],
});

export const CommonService = {
  async getCommonData() {
    const citiesCache = await getFromDB("common_data_cities");
    if (!citiesCache) {
      const query = supabase.from(_COMMON).select("*").single();
      const { data: commonData } = await query;
      if (commonData) {
        await Promise.all(
          Object.entries(commonData).map(([key, value]) =>
            saveToDB(`common_data_${key}`, value)
          )
        );
      }
    }

    const keys = [
      "countries",
      "states",
      "cities",
      "categories",
      "languages",
      "nationalities",
      "professions",
      "tags",
    ];

    await Promise.all(
      keys.map(async (key) => {
        const data = await getFromDB(`common_data_${key}`);
        if (data) CommonData[key] = data;
      })
    );
  },

  async getCountries(filter = {}, orderBy = { field: "name", order: "asc" }, page) {
    return filterData(CommonData.countries, filter, orderBy, page);
  },

  async getStates(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.states, filter, orderBy, page);
  },

  async getCities(filter = {}, orderBy = {}, page) {
    let filtered = CommonData.cities;
    if(filter.search_name) {
      filtered = filtered.filter((v) => v.name.toLowerCase().includes(filter.search_name.toLowerCase()));
    }
    if (filter.country_id) {
      filtered = filtered.filter((v) => v.country_id == filter.country_id);
    }
    if (filter.state_id) {
      filtered = filtered.filter((v) => v.state_id == filter.state_id);
    }
    return filterData(filtered, {}, orderBy, page);
  },

  async getCategories(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.categories, filter, orderBy, page);
  },

  async getLanguages(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.languages, filter, orderBy, page);
  },

  async getNationalities(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.nationalities, filter, orderBy, page);
  },

  async getProfessions(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.professions, filter, orderBy, page);
  },

  async getTags(filter = {}, orderBy = {}, page) {
    return filterData(CommonData.tags, filter, orderBy, page);
  },
};

function filterData(data, filter, orderBy, page) {
  const result = { data: null, count: 0, error: null };
  if (!data || data.length === 0) return result;

  let filtered = data;

  if (filter.search_name) {
    filtered = filtered.filter((v) =>
      v.name.toLowerCase().includes(filter.search_name.toLowerCase())
    );
  }
  if (filter.ids) {
    filtered = filtered.filter((v) => filter.ids.includes(v.id));
  }

  if (orderBy && orderBy.field) {
    filtered = filtered.sort((a, b) => {
      if (a[orderBy.field] < b[orderBy.field]) {
        return orderBy.order === "asc" ? -1 : 1;
      }
      if (a[orderBy.field] > b[orderBy.field]) {
        return orderBy.order === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  if (page > -1) {
    const { from, to } = getPagination(page, 20);
    filtered = filtered.slice(from, to);
  }

  result.data = filtered;
  result.count = filtered.length;
  return result;
}
